<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        Tickets de venta
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="search"
              label="Buscar por ticket, folio o cliente"
              append-icon="mdi-magnify"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model="dateFrom"
              label="Fecha inicio"
              type="date"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model="dateTo"
              label="Fecha fin"
              type="date"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="host"
              :items="cashRegisters"
              item-text="name"
              item-value="id"
              label="Caja"
              clearable
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn text color="primary" @click="resetFilters">Limpiar</v-btn>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="tickets"
          :server-items-length="totalItems"
          :options.sync="options"
          :loading="loading"
          loading-text="Cargando tickets..."
          no-data-text="No se encontraron tickets"
          class="elevation-1"
        >
          <template #[`item.dateNew`]="{ item }">
            {{ formatDate(item.dateNew) }}
          </template>
          <template #[`item.productsCount`]="{ item }">
            {{ Number(item.productsCount || 0).toFixed(2) }}
          </template>
          <template #[`item.total`]="{ item }">
            ${{ Number(item.total || 0).toFixed(2) }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn small text color="primary" @click="openTicketDetail(item)">
              Ver detalle
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="detailDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Detalle de ticket {{ selectedTicketDetail.ticket?.ticketNumber || '-' }}</span>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense v-if="selectedTicketDetail.ticket">
            <v-col cols="12" md="4"><strong>Cliente:</strong> {{ selectedTicketDetail.ticket.customerName }}</v-col>
            <v-col cols="12" md="4"><strong>Productos:</strong> {{ Number(selectedTicketDetail.ticket.productsCount || 0).toFixed(2) }}</v-col>
            <v-col cols="12" md="4"><strong>Total:</strong> ${{ Number(selectedTicketDetail.ticket.total || 0).toFixed(2) }}</v-col>
          </v-row>
          <v-data-table
            :headers="detailHeaders"
            :items="selectedTicketDetail.lines"
            :loading="loadingDetail"
            dense
            disable-pagination
            hide-default-footer
            no-data-text="Sin líneas de ticket"
            class="mt-3"
          >
            <template #[`item.units`]="{ item }">
              {{ Number(item.units || 0).toFixed(2) }}
            </template>
            <template #[`item.price`]="{ item }">
              ${{ Number(item.price || 0).toFixed(2) }}
            </template>
            <template #[`item.subtotal`]="{ item }">
              ${{ Number(item.subtotal || 0).toFixed(2) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import * as salesService from '@/services/salesService';
import { formatDateTimeByTimezone } from '@/utils/dateTime';

export default {
  name: 'SalesTicketsView',
  data() {
    return {
      loading: false,
      search: '',
      dateFrom: '',
      dateTo: '',
      host: null,
      cashRegisters: [],
      debounceTimer: null,
      options: {
        page: 1,
        itemsPerPage: 20,
      },
      tickets: [],
      totalItems: 0,
      detailDialog: false,
      loadingDetail: false,
      selectedTicketDetail: {
        ticket: null,
        lines: [],
      },
      headers: [
        { text: 'Fecha', value: 'dateNew', sortable: false },
        { text: 'Folio', value: 'ticketNumber', sortable: false },
        { text: 'Cliente', value: 'customerName', sortable: false },
        { text: 'Productos', value: 'productsCount', sortable: false, align: 'end' },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      detailHeaders: [
        { text: '#', value: 'line', sortable: false, width: '60px' },
        { text: 'Producto', value: 'productName', sortable: false },
        { text: 'Cantidad', value: 'units', sortable: false, align: 'end' },
        { text: 'Precio', value: 'price', sortable: false, align: 'end' },
        { text: 'Subtotal', value: 'subtotal', sortable: false, align: 'end' },
      ],
    };
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchTickets();
      },
    },
    search() {
      this.debounceFetch();
    },
    dateFrom() {
      this.debounceFetch();
    },
    dateTo() {
      this.debounceFetch();
    },
    host() {
      this.debounceFetch();
    },
  },
  mounted() {
    this.loadCashRegisters();
    this.fetchTickets();
  },
  methods: {
    formatDate(value) {
      return formatDateTimeByTimezone(value, '-');
    },
    debounceFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.fetchTickets();
      }, 350);
    },
    resetFilters() {
      this.search = '';
      this.dateFrom = '';
      this.dateTo = '';
      this.host = null;
      this.options.page = 1;
      this.fetchTickets();
    },
    async loadCashRegisters() {
      try {
        this.cashRegisters = await salesService.fetchCashRegisters();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar cajas.', type: 'error' });
      }
    },
    async fetchTickets() {
      this.loading = true;
      try {
        const data = await salesService.fetchSalesTickets({
          ...this.options,
          q: this.search || undefined,
          dateFrom: this.dateFrom || undefined,
          dateTo: this.dateTo || undefined,
          host: this.host || undefined,
        });
        this.tickets = data.tickets || [];
        this.totalItems = data.totalCount || 0;
      } catch (error) {
        this.tickets = [];
        this.totalItems = 0;
        this.$emit('show-alert', { message: 'Error al cargar tickets de venta.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    async openTicketDetail(ticket) {
      this.detailDialog = true;
      this.loadingDetail = true;
      this.selectedTicketDetail = { ticket: null, lines: [] };
      try {
        this.selectedTicketDetail = await salesService.fetchSalesTicketDetail(ticket.receiptId);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar detalle del ticket.', type: 'error' });
      } finally {
        this.loadingDetail = false;
      }
    },
  },
};
</script>
